import { apiInstance, gql } from "@/services/httpAdapter";
import { errorLogger } from "@Common/helpers/logger";
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { IGetCallActivityResponse } from "@/DataPages/types/callActivity/model";

export async function getCallActivity(locationId: string, commissionCycleId: string, userId: string, skip: number, pageSize: number) {
	const query = gql`
		query getCallActivity($locationId: String!, $commissionCycleId: String!, $userId: String, $skip: Int!, $pageSize: Int!) {
			getCallActivity(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				userId: $userId
				skip: $skip
				pageSize: $pageSize
			) {
				callActivities {
					_id
					extension
					queue
					userId
					isVoiceMail
					isQueue
					externalPhone
					callType
					customerName
					dialedNumberName
					dialedNumber
					callerIdName
					timestamp
					ringDuration
					talkDuration
					holdDuration
					totalDuration
					result
					recorded
					callId
					individualEvaluation {
						turnaroundCallId
						missHandled
						isUsable
						isOnTime
						turnaroundTime
					}
					queueEvaluation {
						turnaroundCallId
						missHandled
						isUsable
						isOnTime
						turnaroundTime
					}
					isValidRingDuration
					isValidTalkDuration
					isValidHoldDuration
					isValidTotalDuration
					isLastEntryOfCallId
					isValidCallDate
					isValidCallNumber
				}
				hasNext
			}
		}
	`;

	const variables = {
		commissionCycleId,
		locationId,
		userId,
		skip,
		pageSize,
	};

	try {
		const response: GraphqlQueryResponse<"getCallActivity", IGetCallActivityResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getCallActivity;
	} catch (err) {
		errorLogger(err as Error, { function: "getCallActivity", message: "Failed to get call activity", variables });
		return null;
	}
}

export async function uploadCallActivity(locationId: string, commissionCycleId: string, file: File) {
	try {
		if (!locationId || !commissionCycleId || !file) {
			return {
				status: false,
				message: "Invalid parameters",
			};
		}

		const query = gql`
			query getCallActivityPresignedUrl($locationId: String!, $commissionCycleId: String!) {
				getCallActivityPresignedUrl(locationId: $locationId, commissionCycleId: $commissionCycleId) {
					fileName
					presignedUrl
				}
			}
		`;

		const variables = {
			locationId,
			commissionCycleId,
		};

		const response: GraphqlQueryResponse<"getCallActivityPresignedUrl", { fileName: string; presignedUrl: string }> =
			await apiInstance.post("/graphql", { query, variables });
		const presigned = response.data?.data?.getCallActivityPresignedUrl;
		if (!presigned) {
			return {
				status: false,
				message: "Failed to get presigned url",
			};
		}

		const fileName = presigned?.fileName;
		const uploadFile = new File([file], fileName);

		const options = {
			headers: {
				"Content-Type": file.type,
			},
		};

		const upload = await apiInstance.put(presigned?.presignedUrl, uploadFile, options);
		const SUCCESS_STATUS_CODE = 200;
		if (upload.status === SUCCESS_STATUS_CODE) {
			return true;
		}

		return {
			status: false,
			message: `Some error occurred`,
		};
	} catch (err) {
		errorLogger(err as Error, {
			function: "uploadCallActivity",
			message: "Failed to upload call activity",
			locationId,
			commissionCycleId,
		});
		return {
			status: false,
			message: `${(err as Error).message}`,
		};
	}
}
